import {
  DownloadRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import { useState } from "react";
import Flx from "../../../components/layout/Flx";

/**
 * Standalone PDF download + optional preview.
 * Does NOT depend on Final Form — you pass data directly.
 *
 * @example
 * // Icon button only
 * <PdfDownloadSection
 *   document={<FinancingRequestPDF />}
 *   data={loanData}
 *   filename="Loan_App.pdf"
 * />
 *
 * // Full button with preview
 * <PdfDownloadSection
 *   document={<FinancingRequestPDF />}
 *   data={loanData}
 *   filename="Loan_App.pdf"
 *   variant="button"
 *   label="Download Loan App"
 *   showPreviewToggle
 * />
 */
const PdfDownloadSection = ({
  document: pdfDocument,
  data,
  convertedData,
  filename = "document.pdf",
  variant = "icon", // "icon" | "button"
  label = "Download PDF",
  showPreviewToggle = false,
  buttonProps = {},
  tooltip = "Download PDF",
  size = "small",
}) => {
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Clone the PDF document with data injected
  const buildPdf = () => {
    if (!pdfDocument) return null;
    return {
      ...pdfDocument,
      props: { ...pdfDocument.props, formData: data, convertedData },
    };
  };

  const handleDownload = async () => {
    const builtPdf = buildPdf();
    if (!builtPdf) return;

    setDownloading(true);
    try {
      const blob = await pdf(builtPdf).toBlob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (variant === "button") {
    return (
      <Button
        variant="outlined"
        size={size}
        startIcon={<DownloadRounded />}
        onClick={handleDownload}
        loading={downloading}
        {...buttonProps}
      >
        {label}
      </Button>
    );
  }

  return (
    <Box>
      <Flx gap={1} ac>
        {variant === "icon" ? (
          <Tooltip title={tooltip}>
            <IconButton
              size={size}
              onClick={handleDownload}
              disabled={downloading}
              {...buttonProps}
            >
              <DownloadRounded fontSize={size} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            size={size}
            startIcon={<DownloadRounded />}
            onClick={handleDownload}
            loading={downloading}
            {...buttonProps}
          >
            {label}
          </Button>
        )}

        {showPreviewToggle && (
          <Button
            variant="outlined"
            size={size}
            startIcon={
              showPreview ? <VisibilityOffRounded /> : <VisibilityRounded />
            }
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview ? "Hide Preview" : "Preview PDF"}
          </Button>
        )}
      </Flx>

      {showPreviewToggle && (
        <Collapse in={showPreview}>
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              overflow: "hidden",
              height: 600,
            }}
          >
            <PDFViewer
              width="100%"
              height="100%"
              showToolbar
              style={{ border: "none" }}
            >
              {buildPdf()}
            </PDFViewer>
          </Paper>
        </Collapse>
      )}
    </Box>
  );
};

export default PdfDownloadSection;
